import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { localize2WithPath, localizeWithPath } from 'vscode/vscode/vs/nls';
import { CancellationToken } from 'vscode/vscode/vs/base/common/cancellation';
import { DisposableStore, Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { registerEditorAction, EditorAction } from 'vscode/vscode/vs/editor/browser/editorExtensions';
import { IBulkEditService, ResourceTextEdit } from 'vscode/vscode/vs/editor/browser/services/bulkEditService';
import { EditorContextKeys } from 'vscode/vscode/vs/editor/common/editorContextKeys';
import { IEditorWorkerService } from 'vscode/vscode/vs/editor/common/services/editorWorker';
import { ILanguageFeaturesService } from 'vscode/vscode/vs/editor/common/services/languageFeatures';
import { ITextModelService } from 'vscode/vscode/vs/editor/common/services/resolverService';
import { getDocumentFormattingEditsUntilResult, formatDocumentWithSelectedProvider } from 'vscode/vscode/vs/editor/contrib/format/browser/format';
import { registerAction2, Action2, MenuId } from 'vscode/vscode/vs/platform/actions/common/actions';
import { ContextKeyExpr } from 'vscode/vscode/vs/platform/contextkey/common/contextkey';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { Progress } from 'vscode/vscode/vs/platform/progress/common/progress';
import { NOTEBOOK_ACTIONS_CATEGORY } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/controller/coreActions';
import { getNotebookEditorFromEditorPane } from 'vscode/vscode/vs/workbench/contrib/notebook/browser/notebookBrowser';
import { NOTEBOOK_IS_ACTIVE_EDITOR, NOTEBOOK_EDITOR_EDITABLE } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookContextKeys';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { INotebookExecutionService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookExecutionService.service';
import { NotebookSetting } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookCommon';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { Extensions } from 'vscode/vscode/vs/workbench/common/contributions';
import { INotebookService } from 'vscode/vscode/vs/workbench/contrib/notebook/common/notebookService.service';

const _moduleId = "vs/workbench/contrib/notebook/browser/contrib/format/formatting";
registerAction2(class extends Action2 {
    constructor() {
        super({
            id: 'notebook.format',
            title: ( localize2WithPath(_moduleId, 0, 'Format Notebook')),
            category: NOTEBOOK_ACTIONS_CATEGORY,
            precondition: ( (ContextKeyExpr.and(NOTEBOOK_IS_ACTIVE_EDITOR, NOTEBOOK_EDITOR_EDITABLE))),
            keybinding: {
                when: ( (EditorContextKeys.editorTextFocus.toNegated())),
                primary: 1024  | 512  | 36 ,
                linux: { primary: 2048  | 1024  | 39  },
                weight: 200
            },
            f1: true,
            menu: {
                id: MenuId.EditorContext,
                when: ( (ContextKeyExpr.and(
                    EditorContextKeys.inCompositeEditor,
                    EditorContextKeys.hasDocumentFormattingProvider
                ))),
                group: '1_modification',
                order: 1.3
            }
        });
    }
    async run(accessor) {
        const editorService = accessor.get(IEditorService);
        const textModelService = accessor.get(ITextModelService);
        const editorWorkerService = accessor.get(IEditorWorkerService);
        const languageFeaturesService = accessor.get(ILanguageFeaturesService);
        const bulkEditService = accessor.get(IBulkEditService);
        const editor = getNotebookEditorFromEditorPane(editorService.activeEditorPane);
        if (!editor || !editor.hasModel()) {
            return;
        }
        const notebook = editor.textModel;
        const disposable = ( (new DisposableStore()));
        try {
            const allCellEdits = await Promise.all(( (notebook.cells.map(async (cell) => {
                const ref = await textModelService.createModelReference(cell.uri);
                disposable.add(ref);
                const model = ref.object.textEditorModel;
                const formatEdits = await getDocumentFormattingEditsUntilResult(editorWorkerService, languageFeaturesService, model, model.getOptions(), CancellationToken.None);
                const edits = [];
                if (formatEdits) {
                    for (const edit of formatEdits) {
                        edits.push(( (new ResourceTextEdit(model.uri, edit, model.getVersionId()))));
                    }
                    return edits;
                }
                return [];
            }))));
            await bulkEditService.apply( allCellEdits.flat(), { label: ( localizeWithPath(_moduleId, 1, "Format Notebook")), code: 'undoredo.formatNotebook', });
        }
        finally {
            disposable.dispose();
        }
    }
});
registerEditorAction(class FormatCellAction extends EditorAction {
    constructor() {
        super({
            id: 'notebook.formatCell',
            label: ( localizeWithPath(_moduleId, 2, "Format Cell")),
            alias: 'Format Cell',
            precondition: ( (ContextKeyExpr.and(
                NOTEBOOK_IS_ACTIVE_EDITOR,
                NOTEBOOK_EDITOR_EDITABLE,
                EditorContextKeys.inCompositeEditor,
                EditorContextKeys.writable,
                EditorContextKeys.hasDocumentFormattingProvider
            ))),
            kbOpts: {
                kbExpr: ( (ContextKeyExpr.and(EditorContextKeys.editorTextFocus))),
                primary: 1024  | 512  | 36 ,
                linux: { primary: 2048  | 1024  | 39  },
                weight: 100
            },
            contextMenuOpts: {
                group: '1_modification',
                order: 1.301
            }
        });
    }
    async run(accessor, editor) {
        if (editor.hasModel()) {
            const instaService = accessor.get(IInstantiationService);
            await instaService.invokeFunction(formatDocumentWithSelectedProvider, editor, 1 , Progress.None, CancellationToken.None, true);
        }
    }
});
let FormatOnCellExecutionParticipant = class FormatOnCellExecutionParticipant {
    constructor(bulkEditService, languageFeaturesService, textModelService, editorWorkerService, configurationService, _notebookService) {
        this.bulkEditService = bulkEditService;
        this.languageFeaturesService = languageFeaturesService;
        this.textModelService = textModelService;
        this.editorWorkerService = editorWorkerService;
        this.configurationService = configurationService;
        this._notebookService = _notebookService;
    }
    async onWillExecuteCell(executions) {
        const enabled = this.configurationService.getValue(NotebookSetting.formatOnCellExecution);
        if (!enabled) {
            return;
        }
        const disposable = ( (new DisposableStore()));
        try {
            const allCellEdits = await Promise.all(( (executions.map(async (cellExecution) => {
                const nbModel = this._notebookService.getNotebookTextModel(cellExecution.notebook);
                if (!nbModel) {
                    return [];
                }
                let activeCell;
                for (const cell of nbModel.cells) {
                    if (cell.handle === cellExecution.cellHandle) {
                        activeCell = cell;
                        break;
                    }
                }
                if (!activeCell) {
                    return [];
                }
                const ref = await this.textModelService.createModelReference(activeCell.uri);
                disposable.add(ref);
                const model = ref.object.textEditorModel;
                const formatEdits = await getDocumentFormattingEditsUntilResult(this.editorWorkerService, this.languageFeaturesService, model, model.getOptions(), CancellationToken.None);
                const edits = [];
                if (formatEdits) {
                    edits.push(...( (formatEdits.map(
                        edit => ( (new ResourceTextEdit(model.uri, edit, model.getVersionId())))
                    ))));
                    return edits;
                }
                return [];
            }))));
            await this.bulkEditService.apply( allCellEdits.flat(), { label: ( localizeWithPath(_moduleId, 3, "Format Cells")), code: 'undoredo.notebooks.onWillExecuteFormat', });
        }
        finally {
            disposable.dispose();
        }
    }
};
FormatOnCellExecutionParticipant = ( (__decorate([
    ( (__param(0, IBulkEditService))),
    ( (__param(1, ILanguageFeaturesService))),
    ( (__param(2, ITextModelService))),
    ( (__param(3, IEditorWorkerService))),
    ( (__param(4, IConfigurationService))),
    ( (__param(5, INotebookService)))
], FormatOnCellExecutionParticipant)));
let CellExecutionParticipantsContribution = class CellExecutionParticipantsContribution extends Disposable {
    constructor(instantiationService, notebookExecutionService) {
        super();
        this.instantiationService = instantiationService;
        this.notebookExecutionService = notebookExecutionService;
        this.registerKernelExecutionParticipants();
    }
    registerKernelExecutionParticipants() {
        this._register(this.notebookExecutionService.registerExecutionParticipant(this.instantiationService.createInstance(FormatOnCellExecutionParticipant)));
    }
};
CellExecutionParticipantsContribution = ( (__decorate([
    ( (__param(0, IInstantiationService))),
    ( (__param(1, INotebookExecutionService)))
], CellExecutionParticipantsContribution)));
const workbenchContributionsRegistry = ( (Registry.as(Extensions.Workbench)));
workbenchContributionsRegistry.registerWorkbenchContribution(CellExecutionParticipantsContribution, 3 );

export { CellExecutionParticipantsContribution };
